import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Trash2, RotateCcw, Briefcase, Users, AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";

const RETENTION_DAYS = 30;

function daysLeft(deletedAt) {
  if (!deletedAt) return RETENTION_DAYS;
  const ts = new Date(deletedAt).getTime();
  const elapsedMs = Date.now() - ts;
  const remaining = RETENTION_DAYS - Math.floor(elapsedMs / 86400000);
  return Math.max(remaining, 0);
}

export default function TrashPage() {
  const [tab, setTab] = useState("commesse");
  const [commesse, setCommesse] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/trash/commesse"),
        api.get("/trash/employees"),
      ]);
      setCommesse(a.data || []);
      setEmployees(b.data || []);
    } catch (e) {
      console.error("[trash] load failed:", e);
      toast.error("Impossibile caricare il cestino");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const restore = async (kind, id) => {
    try {
      await api.post(`/${kind}/${id}/restore`);
      toast.success("Ripristinato");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Ripristino non riuscito";
      toast.error(typeof detail === "string" ? detail : "Ripristino non riuscito");
    }
  };

  const purge = async (kind, id, label) => {
    if (
      !window.confirm(
        `Eliminare definitivamente "${label}"?\nL'operazione NON è reversibile.`
      )
    )
      return;
    try {
      await api.delete(`/trash/${kind}/${id}`);
      toast.success("Eliminato definitivamente");
      load();
    } catch (e) {
      console.error("[trash] purge failed:", e);
      toast.error("Eliminazione non riuscita");
    }
  };

  const items = tab === "commesse" ? commesse : employees;
  const totalCount = commesse.length + employees.length;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
          Anagrafica
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          Cestino
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Gli elementi cancellati restano qui per {RETENTION_DAYS} giorni, poi
          vengono eliminati automaticamente.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={tab === "commesse" ? "default" : "outline"}
          onClick={() => setTab("commesse")}
          className={tab === "commesse" ? "bg-[#0044FF] hover:bg-[#0033CC] h-11" : "h-11"}
          data-testid="tab-commesse"
        >
          <Briefcase className="h-4 w-4 mr-2" />
          Commesse ({commesse.length})
        </Button>
        <Button
          variant={tab === "employees" ? "default" : "outline"}
          onClick={() => setTab("employees")}
          className={tab === "employees" ? "bg-[#0044FF] hover:bg-[#0033CC] h-11" : "h-11"}
          data-testid="tab-employees"
        >
          <Users className="h-4 w-4 mr-2" />
          Dipendenti ({employees.length})
        </Button>
      </div>

      {/* Empty state */}
      {!loading && totalCount === 0 && (
        <Card className="p-10 text-center" data-testid="trash-empty">
          <Trash2 className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading font-bold text-lg mt-3">
            Cestino vuoto
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Niente da ripristinare per ora.
          </p>
        </Card>
      )}

      {/* List */}
      {!loading && items.length > 0 && (
        <div className="space-y-3">
          {items.map((it) => {
            const dLeft = daysLeft(it.deleted_at);
            const expiring = dLeft <= 3;
            const label = tab === "commesse"
              ? it.codice
              : `${it.cognome} ${it.nome}`;
            return (
              <Card
                key={it.id}
                className={`p-4 md:p-5 ${expiring ? "border-orange-300 bg-orange-50/40" : ""}`}
                data-testid={`trash-item-${it.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 shrink-0 rounded-md bg-slate-900 text-white grid place-items-center">
                    {tab === "commesse" ? (
                      <Briefcase className="h-5 w-5" />
                    ) : (
                      <Users className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-base truncate">
                      {label}
                    </div>
                    <div className="text-xs text-slate-600 truncate">
                      {tab === "commesse" ? (it.descrizione || "—") : (it.ruolo || "—")}
                    </div>
                    <div className="text-xs mt-1 flex items-center gap-1.5">
                      {expiring && (
                        <AlertTriangle className="h-3 w-3 text-orange-600" />
                      )}
                      <span className={expiring ? "text-orange-700 font-bold" : "text-slate-500"}>
                        Eliminato il{" "}
                        {it.deleted_at
                          ? new Date(it.deleted_at).toLocaleDateString("it-IT")
                          : "?"}
                        {" · "}
                        {dLeft === 0
                          ? "scadrà oggi"
                          : `restano ${dLeft} giorn${dLeft === 1 ? "o" : "i"}`}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-11 text-green-700 hover:bg-green-50 hover:text-green-700 border-green-300"
                      onClick={() => restore(tab, it.id)}
                      data-testid={`restore-${it.id}`}
                    >
                      <RotateCcw className="h-4 w-4 md:mr-2" />
                      <span className="hidden md:inline">Ripristina</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-11 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                      onClick={() => purge(tab, it.id, label)}
                      data-testid={`purge-${it.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="text-center text-slate-500 py-10">Caricamento…</div>
      )}
    </div>
  );
}
